import React, { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import axios from "axios";
import { API_BASE_URL } from "@/config/api";
import { authService } from "@/services/authService";
import Loader from "@/components/Loader";
import { isOwnerRole } from "@/utils/roles";
import styles from "./buffer.module.css";

export default function BufferPage() {
  const [bufferConfig, setBufferConfig] = useState({
    sports: { MATCH_ODDS: 0, Bookmaker: 0, Normal: 0, others: 0 },
    casino: 0
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'success' });

  // Get active role to ensure only owner can access
  const activeRole = localStorage.getItem('activeRole');
  const isOwner = isOwnerRole(activeRole);

  useEffect(() => {
    if (isOwner) {
      fetchConfig();
    }
  }, [isOwner]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const profile = await authService.getOwnerProfile();
      if (profile && profile.success && profile.data) {
        const config = profile.data.platform_configurations;
        let parsedConfig = config || {};
        
        // Handle if string
        if (typeof config === 'string') {
          try { 
            parsedConfig = JSON.parse(config); 
          } catch(e) {
            console.error("Error parsing config JSON", e);
          }
        }
        
        const val = parsedConfig.buffer_time;
        if (typeof val === 'number') {
             // Legacy: apply global value to all
             setBufferConfig({
                 sports: { MATCH_ODDS: val, Bookmaker: val, Normal: val, others: val },
                 casino: val
             });
        } else if (val && typeof val === 'object') {
             setBufferConfig({
                 sports: {
                     MATCH_ODDS: val.sports?.MATCH_ODDS || 0,
                     Bookmaker: val.sports?.Bookmaker || 0,
                     Normal: val.sports?.Normal || 0,
                     others: val.sports?.others || 0
                 },
                 casino: val.casino || 0
             });
        }
      }
    } catch (error) {
      console.error("Failed to fetch buffer config", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!isOwner) return;
    setSaving(true);
    setAlert({ show: false, message: '', type: '' });

    try {
      // Get owner token
      const token = localStorage.getItem('ownerToken');
      
      // Sanitize config to ensure numbers e.g. convert '' back to 0
      const sanitizedConfig = {
        sports: {
          MATCH_ODDS: Number(bufferConfig.sports.MATCH_ODDS) || 0,
          Bookmaker: Number(bufferConfig.sports.Bookmaker) || 0,
          Normal: Number(bufferConfig.sports.Normal) || 0,
          others: Number(bufferConfig.sports.others) || 0
        },
        casino: Number(bufferConfig.casino) || 0
      };

      const response = await axios.put(
        `${API_BASE_URL}/admin/owner/update-config`, 
        { buffer_time: sanitizedConfig }, 
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );

      if (response.data.success) {
        setAlert({ show: true, message: 'Buffer time updated successfully!', type: 'success' });
        // Hide alert after 3 seconds
        setTimeout(() => setAlert({ show: false, message: '', type: '' }), 3000);
      } else {
        throw new Error(response.data.message || 'Update failed');
      }
    } catch(error) {
      console.error("Update failed", error);
      setAlert({ 
        show: true, 
        message: error.response?.data?.error || error.message || 'Failed to update configuration', 
        type: 'error' 
      });
    } finally {
      setSaving(false);
    }
  };

  if (!isOwner) {
    return (
      <Layout>
        <div className={styles.accessDeniedContainer}>
          <h2 className={styles.accessDeniedText}>Access Denied: Only Owner can access this page.</h2>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.container}>
        {/* Header Section (Matching NetExposure style) */}
        <div className={styles.headerContainer}>
           <div className={styles.headerWrapper}>
            <div className={styles.titleWrapper}>
              <div className={styles.title}>
                Risk Management - Buffer Configuration
              </div>
              <div className={styles.divider}></div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className={styles.contentContainer}>
          {loading ? (
             <div className={styles.loadingText}>Loading configuration...</div>
          ) : (
            <div className={styles.formWrapper}>
              
              {alert.show && (
                <div className={`${styles.alert} ${alert.type === 'success' ? styles.alertSuccess : styles.alertError}`}>
                  {alert.message}
                </div>
              )}

              <div className={styles.formGroup}>
                <h3 className={styles.sectionTitle} style={{marginTop: '0'}}>Sports Configuration</h3>
                
                <div className={styles.gridContainer} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <label className={styles.labelGroup}>
                    <span className={styles.labelText}>Match Odds (seconds)</span>
                    <input 
                      type="number" 
                      min="0"
                      value={bufferConfig.sports.MATCH_ODDS}
                      onChange={(e) => setBufferConfig({
                        ...bufferConfig,
                        sports: { ...bufferConfig.sports, MATCH_ODDS: e.target.value === '' ? '' : Number(e.target.value) }
                      })}
                      className={styles.inputField}
                    />
                  </label>

                  <label className={styles.labelGroup}>
                    <span className={styles.labelText}>Bookmaker (seconds)</span>
                    <input 
                      type="number" 
                      min="0"
                      value={bufferConfig.sports.Bookmaker}
                      onChange={(e) => setBufferConfig({
                        ...bufferConfig,
                        sports: { ...bufferConfig.sports, Bookmaker: e.target.value === '' ? '' : Number(e.target.value) }
                      })}
                      className={styles.inputField}
                    />
                  </label>

                  <label className={styles.labelGroup}>
                    <span className={styles.labelText}>Normal/Fancy (seconds)</span>
                    <input 
                      type="number" 
                      min="0"
                      value={bufferConfig.sports.Normal}
                      onChange={(e) => setBufferConfig({
                        ...bufferConfig,
                        sports: { ...bufferConfig.sports, Normal: e.target.value === '' ? '' : Number(e.target.value) }
                      })}
                      className={styles.inputField}
                    />
                  </label>

                   <label className={styles.labelGroup}>
                    <span className={styles.labelText}>Others (seconds)</span>
                    <input 
                      type="number" 
                      min="0"
                      value={bufferConfig.sports.others}
                      onChange={(e) => setBufferConfig({
                        ...bufferConfig,
                        sports: { ...bufferConfig.sports, others: e.target.value === '' ? '' : Number(e.target.value) }
                      })}
                      className={styles.inputField}
                    />
                  </label>
                </div>

                <hr className={styles.divider} style={{ margin: '2rem 0', opacity: 0.1 }} />

                <h3 className={styles.sectionTitle}>Casino Configuration</h3>
                <label className={styles.labelGroup}>
                  <span className={styles.labelText}>Casino Buffer (seconds)</span>
                  <input 
                    type="number" 
                    min="0"
                    value={bufferConfig.casino}
                    onChange={(e) => setBufferConfig({
                      ...bufferConfig,
                      casino: e.target.value === '' ? '' : Number(e.target.value)
                    })}
                    className={styles.inputField}
                  />
                </label>

                <button
                  onClick={handleUpdate}
                  disabled={saving}
                  className={`${styles.updateButton} ${saving ? styles.buttonDisabled : styles.buttonActive}`}
                >
                  {saving ? 'Updating...' : 'Update Buffer'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
